import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, VariantProps } from 'class-variance-authority'
import clsx from 'clsx'
import React, { ButtonHTMLAttributes, useMemo } from 'react'

import { textVariants } from '@/components-latest/primitives/Text'
import OldSpinner from '@/components-latest/shared/OldSpinner'

import Styles from './Button.module.scss'

const buttonVariants = cva(Styles.buttonBase, {
  variants: {
    variant: {
      positive: Styles.positive,
      branded: Styles.branded,
      primary: Styles.primary,
      secondary: Styles.secondary,
      tertiary: Styles.tertiary,
      destructive: Styles.destructive,
      transparent: Styles.transparent,
      transparentOutlined: Styles.transparentOutlined
    },
    size: {
      pill: clsx(Styles.pill, textVariants({ variant: 'caption' })),
      smaller: clsx(Styles.smaller, textVariants({ variant: 'body' })),
      small: clsx(Styles.small, textVariants({ variant: 'body' })),
      medium: clsx(Styles.medium, textVariants({ variant: 'subtitle' })),
      large: clsx(Styles.large, textVariants({ variant: 'subtitle' }))
    },
    contentType: {
      textWithIcon: Styles.withIcon,
      onlyIcon: Styles.onlyIcon
    },
    shape: {
      rectangle: Styles.rectangle,
      rounded: Styles.rounded
    }
  },
  compoundVariants: [
    {
      variant: ['branded', 'primary', 'positive'],
      size: 'small',
      className: textVariants({ variant: 'body', weight: 'semiBold' })
    },
    {
      variant: ['branded', 'primary', 'positive'],
      size: ['medium', 'large'],
      className: textVariants({ variant: 'subtitle', weight: 'bold' })
    }
  ],
  defaultVariants: {
    size: 'medium'
  }
})

interface IButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof buttonVariants>, 'contentType'> {
  icon?: React.ReactNode
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      className,
      variant,
      size,
      shape,
      icon,
      asChild = false,
      loading = false,
      children,
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : 'button'

    const iconSlot = useMemo(() => {
      if (loading) return <OldSpinner className={Styles.loading} />
      if (icon) return icon
      return null
    }, [icon, loading])

    const contentType = useMemo(() => {
      if (children && icon) return 'textWithIcon'
      if (!children && icon) return 'onlyIcon'
    }, [children, icon])

    return (
      <Component
        className={clsx(
          buttonVariants({ variant, size, shape, contentType, className })
        )}
        data-variant={variant}
        ref={ref}
        {...props}>
        {iconSlot && <span className={Styles.icon}>{iconSlot}</span>}
        <Slottable>{children}</Slottable>
      </Component>
    )
  }
)
Button.displayName = 'Button'

export type { IButtonProps }
export { buttonVariants }
export default Button
